import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../../context/LocalizationContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { useAppContext } from "../../context/AppContext";
import HomeIcon from "../../assets/icons/home.svg";
import HumanIcon from "../../assets/icons/human.svg";
import "./VitalsCheck.css";
import HumanScanVideo from "../../assets/videos/human_scan.mp4";
import Header from "../shared/Header";
import arrow from "../../assets/icons/arrow.svg";
import MicIcon from "../../assets/icons/AI/mic.svg";
import FloatingButton from "../shared/FloatingButton";
import Modal from "../shared/Modal";
import LiveAvatar from "../shared/LiveAvatar";

const VitalsCheck = ({ userData }) => {
  const navigate = useNavigate();
  const { t, isRTL } = useLocalization();
  const { subscribe } = useWebSocket();
  const { measures, setMeasures } = useAppContext();
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);

  // Memoized callback to prevent LiveAvatar from restarting session on parent re-renders
  const handleCloseAvatar = useCallback(() => {
    setShowAvatarModal(false);
  }, []);

  // Localize vital names using backend measurementType key
  const getLocalizedVitalName = (measurementType) => {
    const translated = t(`measureAspects.${measurementType}`);
    // If translation is found (not returning the key itself), use it
    if (translated !== `measureAspects.${measurementType}`) {
      return translated;
    }
    // Fallback: format the raw key as readable text
    return measurementType
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Subscribe to Vitals event from WebSocket
  useEffect(() => {
    const handleVitalsEvent = (data) => {
      console.log("Vitals event received:", data);

      // Extract measurements from the payload
      const measurements =
        data?.payload?.measurements || data?.measurements || [];
      // Map and render all received measurements
      const mappedMeasures = measurements
        .map((m) => {
          const rawName = (m.measurementType || "").trim();

          return {
            name: rawName,
            value: m.value?.toString?.() ?? "",
            unit: m.unit || "",
          };
        })
        .filter((m) => m.name);

      // Update measures in store and stop measuring
      setMeasures(mappedMeasures);
      setIsMeasuring(false);
    };

    // Subscribe to the Vitals event
    const unsubscribe = subscribe("Vitals", handleVitalsEvent);

    return () => {
      unsubscribe();
    };
  }, [subscribe, setMeasures]);

  // Check measures in store on mount
  useEffect(() => {
    console.log("measures from store", measures);
    if (measures && Array.isArray(measures) && measures.length > 0) {
      // Measures exist in store, don't start measuring
      setIsMeasuring(false);
    } else {
      // No measures in store, start loading
      setIsMeasuring(true);
    }
  }, [measures]);

  return (
    <div className="main-container">
      <Header text={t("vitalsCheck.title")} userData={userData} />
      {isMeasuring ? (
        <>
          {/* Video Background for measuring phase */}
          <video className="video-background" autoPlay loop muted playsInline>
            <source src={HumanScanVideo} type="video/mp4" />
          </video>
          <div className="description-container">
            {t("vitalsCheck.instructions")}
          </div>
        </>
      ) : (
        <>
          {/* Results phase with human.svg background */}
          <div className="results-container">
            <div className="human-background">
              <img
                src={HumanIcon}
                alt="Human body scan"
                className="human-svg"
              />
            </div>

            <div className="vitals-grid">
              {measures &&
                Array.isArray(measures) &&
                measures.map((vital, index) => (
                  <div key={index} className="vital-card">
                    <div className="corner-top-right"></div>
                    <div className="corner-bottom-left"></div>
                    <div className="vital-card-header">
                      <div className="vital-name">
                        {getLocalizedVitalName(vital.name)}
                      </div>
                    </div>
                    <div className="vital-value-container">
                      <div className="vital-value">{vital.value}</div>
                      <div className="vital-unit">{vital.unit}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      <div className="assessment-footer">
        <button
          className="footer-btn home-btn"
          onClick={() => navigate("/dashboard")}
        >
          <img src={HomeIcon} alt="Home" />
        </button>

        {!isMeasuring && (
          <div className="footer-btn-container">
            <button
              className="next-btn talk-to-doctor-btn"
              onClick={() => navigate("/assessment")}
            >
              <span>{t("vitalsCheck.proceedToAssessments")}</span>
              <img
                src={arrow}
                alt="Next"
                className={`arrow-icon ${isRTL ? 'rtl' : ''}`}
              />
            </button>
          </div>
        )}
        <FloatingButton
          icon={MicIcon}
          label={t("dashboard.askAI")}
          position="in-footer"
          userData={userData}
          onClick={() => setShowAvatarModal(true)}
        />
      </div>

      {/* Avatar Modal */}
      <Modal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        noScroll={true}
        customLayout={true}
      >
        <div className="avatar-modal-container">
          <LiveAvatar onClose={handleCloseAvatar} />
        </div>
      </Modal>
    </div>
  );
};

export default VitalsCheck;
